'use client'

import ShareTrialModal from '@components/app/ShareTrialModal'
import type { ButtonProps } from '@components/primitives/Button'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { faShare } from '@fortawesome/pro-solid-svg-icons/faShare'
import type { ColorGroup } from '@lib/colors/colors'
import { trackEvent, trackEventWithFacebook } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import type { UtmLookup } from '@prisma/client'
import { useEffect, useState } from 'react'

type ShareTrialModalProps = {
  color?: ColorGroup
  shareTrialCodes: UtmLookup[]
  trialSlug: string
  trialTitle: string
}

export type ShareTrialButtonProps = Omit<ButtonProps, 'children'> & {
  iconClassName?: string
} & ShareTrialModalProps

export const ShareTrialButton = ({
  iconClassName,
  shareTrialCodes,
  trialSlug,
  trialTitle,
  ...buttonProps
}: ShareTrialButtonProps) => {
  const buttonColor = buttonProps.color ?? 'black'

  const [shareTrialModalOpen, setShareTrialModalOpen] = useState(false)

  const toggleShareTrialModalOpen = () => {
    setShareTrialModalOpen(true)
    trackEventWithFacebook(TrackingEvents.SHARE_TRIAL_MODAL_OPENED, {
      source: TrackingSources.LISTING,
    })
  }

  const toggleShareTrialModalClosed = () => {
    setShareTrialModalOpen(false)
    trackEvent(TrackingEvents.SHARE_TRIAL_MODAL_CLOSED, {
      source: TrackingSources.LISTING,
    })
  }

  useEffect(() => {
    const hasShareModal = new URLSearchParams(window.location.search).get(
      'shareModal',
    )
    if (hasShareModal === 'true') {
      toggleShareTrialModalOpen()
    }
  }, [])

  return (
    <>
      <Button
        {...buttonProps}
        className={classMerge(
          buttonClassNames.legacy.outline[buttonColor],
          'w-full rounded-full border border-neutral200 text-neutral600',
          buttonProps.className,
        )}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          toggleShareTrialModalOpen()
        }}
        value={
          <span className='flex w-full flex-row items-center justify-center gap-2'>
            <Icon
              className={classMerge('text-neutral600', iconClassName)}
              icon={faShare}
            />
            Share
          </span>
        }
      />
      <ShareTrialModal
        onClose={toggleShareTrialModalClosed}
        shareTrialCodes={shareTrialCodes ?? []}
        source={TrackingSources.LISTING}
        trialSlug={trialSlug}
        trialTitle={trialTitle}
        visible={shareTrialModalOpen}
      />
    </>
  )
}
